/**
 * Cross-Domain Auth Handoff Utilities
 * 
 * Handles automatic resolution of authCode query parameters for seamless
 * cross-domain authentication.
 * 
 * Flow:
 * 1. Check URL for authCode parameter
 * 2. If present, resolve it with backend
 * 3. Store the returned token (overrides any existing token)
 * 4. Clean the URL (remove authCode)
 * 5. Return resolved customer and context
 */

import { SessionRole } from '../types';
import { setClientToken } from './tokenStorage';

export interface AuthHandoffResolveResponse {
  sessionId: string;
  token: string;
  customer: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    role: SessionRole;
  };
  context: Record<string, unknown>;
}

// Track in-flight and completed resolutions to prevent duplicates
const resolutionCache = new Map<string, Promise<AuthHandoffResolveResponse>>();
const resolvedCodes = new Set<string>();

/**
 * Check if authCode is present in URL
 */
export function hasAuthCode(): boolean {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('authCode');
}

/**
 * Get authCode from URL
 */
export function getAuthCode(): string | null {
  if (typeof window === 'undefined') return null;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('authCode');
}

/**
 * Check if a code has already been resolved
 */
export function isCodeAlreadyResolved(code: string): boolean {
  return resolvedCodes.has(code);
}

/**
 * Resolve auth handoff and return token + customer info
 * 
 * This function:
 * 1. Calls POST /api/v1/cms/auth/resolve-handoff
 * 2. Stores the returned token (overrides existing)
 * 3. Cleans the URL (removes authCode)
 * 4. Returns customer and context data
 * 
 * 🔒 Deduplication: Multiple calls with the same code will return the same promise
 * to prevent duplicate API requests (e.g., React StrictMode double-mounting)
 */
export async function resolveAuthHandoff(
  authCode: string,
  storeId: string,
  apiBaseUrl: string,
  debugMode = false
): Promise<AuthHandoffResolveResponse> {
  // Check if already resolved
  if (resolvedCodes.has(authCode)) {
    if (debugMode) {
      console.log('[AuthHandoff] Code already resolved, skipping duplicate request');
    }
    throw new Error('Auth code already resolved');
  }

  // Check if resolution is in-flight
  const inFlightResolution = resolutionCache.get(authCode);
  if (inFlightResolution) {
    if (debugMode) {
      console.log('[AuthHandoff] Resolution already in progress, waiting for existing request');
    }
    return inFlightResolution;
  }

  if (debugMode) {
    console.log('[AuthHandoff] Resolving authCode:', authCode.substring(0, 15) + '...');
  }

  // Create resolution promise
  const resolutionPromise = (async () => {
    try {
      // Call resolve endpoint (no authentication required)
      const response = await fetch(`${apiBaseUrl}/api/v1/cms/auth/resolve-handoff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: authCode,
          storeId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `Failed to resolve auth handoff: ${response.status}`);
      }

      const data: AuthHandoffResolveResponse = await response.json();

      if (debugMode) {
        console.log('[AuthHandoff] ✅ Resolved successfully:', {
          customerId: data.customer.id,
          role: data.customer.role,
          hasContext: Object.keys(data.context).length > 0,
        });
      }

      // Store token (overrides any existing token)
      if (debugMode) {
        console.log('[AuthHandoff] Storing new token (overriding existing)');
      }
      setClientToken(data.token);

      // Clean URL (remove authCode parameter)
      cleanAuthCodeFromUrl(debugMode);

      // Mark as resolved
      resolvedCodes.add(authCode);

      return data;
    } catch (error) {
      console.error('[AuthHandoff] ❌ Failed to resolve:', error);
      throw error;
    } finally {
      // Remove from in-flight cache after completion (success or failure)
      resolutionCache.delete(authCode);
    }
  })();

  // Cache the in-flight promise
  resolutionCache.set(authCode, resolutionPromise);

  return resolutionPromise;
}

/**
 * Remove authCode from URL without page reload
 * Uses history.replaceState to update URL cleanly
 */
export function cleanAuthCodeFromUrl(debugMode = false): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);

  if (url.searchParams.has('authCode')) {
    url.searchParams.delete('authCode');

    // Use replaceState to update URL without reload
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);

    if (debugMode) {
      console.log('[AuthHandoff] Cleaned authCode from URL');
    }
  }
}

/**
 * Check if we should resolve authCode
 * Returns true if authCode is present and valid format
 */
export function shouldResolveAuthCode(): boolean {
  const authCode = getAuthCode();
  if (!authCode || !authCode.startsWith('ah_')) {
    return false;
  }

  // Don't resolve if already resolved
  if (resolvedCodes.has(authCode)) {
    return false;
  }

  return true;
}

